import { AxiosResponse }                    from "axios";
import { ChangeEvent, useEffect, useState } from "react";
import { ToggleButton, ToggleButtonGroup }  from "react-bootstrap";
import { NavLink, useNavigate }             from "react-router-dom";
/* Project References */
import { DistanceBandSet }                      from "../../../models/Admin/DistanceBands";
import { Contact, ContactType }                 from "../../../models/Admin/Contact";
import { Event2LocationPost, EventPost, Event } from "../../../models/Event";
import msalRestClient                    from "../../../services/clients/MsalRestClient";
import GiscoAutocomplete                 from "../../../components/GiscoAutocomplete";
import Feature                           from "../../../models/GISCO/Feature";
import { PageableCollection }            from "../../../models/PageableCollection";

type FormState = {

    currentStep   : number,
    errors        : string[],
    checkContacts? : number[],
}

const EventCreate = () => {

    // #region Init States

    let initLocation : Event2LocationPost = {
        osmid           : -1,
        name            : "",
        city            : "",
        countryISO_3166 : "",
        latitude        : 0,
        longitude       :0
    }

    let initPostEvent: EventPost = {
        number                : '',
        title                 : '',
        startDate             : new Date(),
        endDate               : new Date(),
        offlineDate           : new Date(),
        distanceBandVersion   : -1,
        accommodationEligible : false,
        maxNights             :  0,
        location              : initLocation
    }

    let emptyCollection: PageableCollection<Contact> = {
        totalPages: 0,
        count     : 0,
        next      : "",
        previous  : "",
        results   : []
    }

    // #endregion

    // #region States & Hooks

    const navigate = useNavigate();

    const [event, setEvent]                 = useState(initPostEvent);
    const [contacts, setContacts]           = useState<PageableCollection<Contact>>(emptyCollection);
    const [distanceBands, setDistanceBands] = useState<DistanceBandSet[]>([]);

    const [formState, setFormState]   = useState<FormState>({ currentStep: 1, errors: [], checkContacts: [] });
    const [searchTerm, setSearchTerm] = useState<string>("");

    useEffect(() => { fetchFromApi() }, [])
    useEffect(() => {

        const timer = setTimeout(() => {

            msalRestClient.get<PageableCollection<Contact>>("/api/contacts?email=" + searchTerm)
                .then(response => onApiSuccess2(response));

        }, 1000)

        return () => clearTimeout(timer);

    }, [searchTerm])

    // #endregion

    // #region Functions

    /**
     * 
     */
    const fetchFromApi = () => {

        Promise.all([
            msalRestClient.get<PageableCollection<Contact>>("/api/contacts"),
            msalRestClient.get<DistanceBandSet[]>("/api/distancebands")
        ])
            .then(response => onApiSuccess(response))
            .catch(error   => console.error(error))
    }

    /**
     * 
     * @param param0
     */
    const onApiSuccess  = ([contactResponse, distanceBandResponse]: [AxiosResponse<PageableCollection<Contact>, any>, AxiosResponse<DistanceBandSet[], any>]) =>
    {
        if (distanceBandResponse.status === 200) { setDistanceBands(distanceBandResponse.data); }
        if (contactResponse.status === 200)      { setContacts(contactResponse.data); }
    }
    const onApiSuccess2 = (contactResponse: AxiosResponse<PageableCollection<Contact>, any>) =>
    {
        if (contactResponse.status === 200) { setContacts(contactResponse.data); }
        else if (contactResponse.status === 204) { setContacts(emptyCollection); }
    }

    /**
     * 
     * @param feature
     */
    const onAutoCompleteChange = (feature: Feature) =>
    {
        setEvent(x => ({
            ...x, // Spread operator to copy data from current state
            location: {
                osmid:           feature.properties.osm_id,
                name:            feature.properties.type === "house" && feature.properties.name ? feature.properties.name : event.location.name,
                city:            feature.properties.type === "city"  && feature.properties.name ? feature.properties.name : feature.properties.city,
                countryISO_3166: feature.properties.countrycode,
                latitude:        feature.geometry.coordinates[1],
                longitude:       feature.geometry.coordinates[0]
            }
        }));
    }

    /**
     * 
     */
    const onFirstMaskSubmit = () =>
    {
        // #region Validation

        let errors: string[] = [];

        if (event.number === '') { errors.push("Field 'Number' is mandatory.") }
        if (event.title  === '') { errors.push("Field 'Title' is mandatory.")  }

        if (!event.startDate) { errors.push("Field 'StartDate' is mandatory.") }
        if (!event.endDate)   { errors.push("Field 'EndDate' is mandatory.")   }

        if (event.distanceBandVersion === -1) { errors.push("Field 'DistanceBandVersion' is mandatory.") }

        // #endregion

        if (errors.length === 0) {
            // Set state to next step and purge errors ...
            setFormState(x => ({ ...x, currentStep: 2, errors: [] }))
        }
        else { setFormState(x => ({ ...x, currentStep: 1, errors: errors })) }
    }

    /**
     * 
     */
    const onSecondMaskSubmit = () =>
    {
        // #region Validation

        let errors: string[] = [];

        if (event.location.name === '')            { errors.push("Field 'Venue Name' is required.") }
        if (event.location.city === '')            { errors.push("Field 'City' is required.") }
        if (event.location.countryISO_3166 === '') { errors.push("Field 'Country' is required.") }

        // #endregion

        if (errors.length === 0) {
            // Set state to next step and purge errors ...
            setFormState(x => ({ ...x, currentStep: 3, errors: [] }))
        }
        else { setFormState(x => ({ ...x, currentStep: 2, errors: errors })) }
    }

    /**
     * 
     */
    const onCheckedStateChange = (event: ChangeEvent<HTMLInputElement> | undefined, id: number) => {

        let checked = formState.checkContacts?.includes(id);

        if (checked)
        {
            let index: number = formState.checkContacts?.indexOf(id) ?? -1;

            let _contacts: number[] = [...formState.checkContacts ?? []];
            _contacts.splice(index, 1);

            setFormState(x => ({
                ...x, // Spread operator to copy data from current state
                checkContacts: _contacts
            }))
        }
        else
        {
            let _contacts: number[] = formState.checkContacts ?? [];
            _contacts.push(id);

            setFormState(x => ({
                ...x, // Spread operator to copy data from current state
                checkContacts: _contacts
            }))
        }
    }

    /**
     * 
     */
    const onSubmit = () =>
    {
        event.contacts = formState.checkContacts; // Set Property wihtout challing state hook to refresh component ...

        msalRestClient.post<Event>("/api/events", event)
            .then(response => {
                navigate("/admin/event/" + response.data.id)
            })
    }

    // #region Render Functions

    /**
     * Method renders alert with missing mandatory field informations.
     * TO-DO: Extract in on components that takes an array with errors as parameter.
     * @returns JSX.Element representing a boostrap alert alert-danger.
     */
    const renderErrorPane = () => {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="alert alert-danger">
                        <p className="fw-bold mb-0">Whoops! There were some problems with your input.</p>
                        <ul className="mb-0">
                            {
                                formState.errors.map((value, index) => {
                                    return (
                                        <li key={"err_" + index}>{value}</li>
                                    )
                                })
                            }
                        </ul>
                    </div>
                </div>
            </div>
        )
    }

    /**
     * Method renders first input mask that allows the user to enter 
     * all needed core event data.
     * @returns: JSX.Element that will be rendered inside the targeted DOM.
     */
    const renderFirstMask = () =>
    {
        return (
            <>
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-info" role="alert">
                            <i className="bi bi-info-circle me-2" />
                            <b>Step 1:</b> Please fill in all required core information for the new form.
                        </div>
                    </div>
                </div>

                {(formState.errors.length > 0) && renderErrorPane()}

                <hr className="mt-0" />

                <div className="row">

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold" htmlFor="number">Number</label>
                        <input type="text" className="form-control" id="number" value={event.number} onChange={e => setEvent(x => ({ ...x, number: e.target.value }))} />
                    </div>

                    <div className="form-group col-12 col-md-8">
                        <label className="fw-bold" htmlFor="title">Title</label>
                        <input type="text" className="form-control" id="title" value={event.title} onChange={e => setEvent(x => ({ ...x, title: e.target.value }))} />
                    </div>

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold" htmlFor="startDate">Seminar Start Date</label>
                        <input type="date" className="form-control" id="startDate" value={event.startDate.toISOString().split('T')[0]} onChange={e => setEvent(x => ({ ...x, startDate: new Date(e.target.value) }))} />
                    </div>

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold" htmlFor="endDate">Seminar End Date</label>
                        <input type="date" className="form-control" id="endDate" value={event.endDate.toISOString().split('T')[0]} onChange={e => setEvent(x => ({ ...x, endDate: new Date(e.target.value) }))} />
                    </div>

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold" htmlFor="offlineDate">Offlinedate</label>
                        <input type="date" className="form-control" id="offlineDate" value={event.offlineDate.toISOString().split('T')[0]} onChange={e => setEvent(x => ({ ...x, offlineDate: new Date(e.target.value) }))} />
                    </div>

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold" htmlFor="distanceBandVersion">Distance Band Version</label>
                        <select className="form-select" id="distanceBandVersion" value={event.distanceBandVersion} onChange={e => setEvent(x => ({ ...x, distanceBandVersion: Number(e.target.value) }))}>
                            <option value={-1}>Please select version</option>
                            {
                                distanceBands.map((value, index) => {
                                    return (
                                        <option key={index}>{value.version}</option>
                                    )
                                })
                            }
                        </select>
                    </div>

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold">Accommodation Eligible</label><span><i className="bi bi-question-circle-fill ms-2"></i></span>
                        <ToggleButtonGroup className="w-100" type="radio" name="accommodation" defaultValue={event.accommodationEligible ? 1 : 0} onChange={(value, e) => setEvent(x => ({ ...x, accommodationEligible: (Number(value) === 1) }))}>
                            <ToggleButton id="btn-radio-1" value={0} className="btn btn-light">
                                No
                            </ToggleButton>
                            <ToggleButton id="btn-radio-2" value={1} className="btn btn-light">
                                Yes
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </div>

                    <div className="form-group col-12 col-md-4">
                        <label className="fw-bold" htmlFor="maxNights">Max. Nights</label><span><i className="bi bi-question-circle-fill ms-2"></i></span>
                        <input type="number" id="maxNights" min={-1} className="form-control" value={event.maxNights} onChange={e => setEvent(x => ({ ...x, maxNights: Number(e.target.value) }))} />
                    </div>

                </div>

                <hr />

                <div className="row">
                    <div className="col-12 text-end">
                        <button className="btn btn-primary" onClick={onFirstMaskSubmit}>
                            Next<i className="bi bi-arrow-right ms-1"></i>
                        </button>
                    </div>
                </div>
            </>
        )
    }

    /**
     * Method renders second input mask that allows the user to enter
     * the required location information.
     * @returns: JSX.Element that will be rendered inside the targeted DOM.
     */
    const renderSecondMask = () =>
    {
        return (
            <>
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-info" role="alert">
                            <i className="bi bi-info-circle me-2" />
                            <b>Step 2:</b> Please select the venue from the GISCO suggestion list. Select either the <b>institution</b> or the <b>city</b> of the conference.
                        </div>
                    </div>
                </div>

                {(formState.errors.length > 0) && renderErrorPane()}

                <hr className="mt-0" />

                <div className="row">

                    <div className="form-group col-12">
                        <label htmlFor="sourceLocation" className="fw-bold">Institution | City</label>
                        <GiscoAutocomplete id="sourceLocation" onFeatureChange={onAutoCompleteChange} />
                    </div>

                    <div className="form-group col-12 col-md-6">
                        <label htmlFor="venueName" className="fw-bold">Venue Name</label>
                        <input type="text" id="venueName" className="form-control" value={event.location.name} onChange={e => setEvent(x => ({ ...x, location: { ...x.location, name: e.target.value } }))} />
                    </div>

                    <div className="form-group col-12 col-md-6">
                        <label htmlFor="city" className="fw-bold">City</label>
                        <input type="text" id="city" className="form-control" disabled={true} value={event.location.city} />
                    </div>

                </div>

                <hr />

                <div className="row">

                    <div className="col-12 col-md-6 text-start">
                        <button className="btn btn-primary" onClick={e => setFormState(x => ({ ...x, currentStep: 1, errors: [] }))}>
                            <i className="bi bi-arrow-left me-1" /> Previous
                        </button>
                    </div>
                    <div className="col-12 col-md-6 text-end">
                        <button className="btn btn-primary" onClick={onSecondMaskSubmit}>
                            Next<i className="bi bi-arrow-right ms-1" />
                        </button>
                    </div>

                </div>
            </>
        )
    }

    /**
     * Methods renders third input mask that allows the user to enter
     * the optional employees informations.
     * @returns
     */
    const renderThirdMask = () =>
    {
        return (
            <>
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-info" role="alert">
                            <p>
                                <i className="bi bi-info-circle me-2" />
                                <b>Step 3:</b> Optional: Please select the contact persons responsible for the event.
                            </p>
                            <button className="btn btn-primary" onClick={e => setFormState({ currentStep: 4, errors: [], checkContacts: [] })}>Skip<i className="bi bi-chevron-double-right ms-1" /></button>
                        </div>
                    </div>
                </div>

                <hr className="mt-0" /> 

                <div className="row">
                    <div className="col-12">

                        <table className="table">
                            <thead>
                                <tr>
                                    <th colSpan={4}>
                                        <div className="form-group col-12">
                                            <label className="fw-bold">Search</label>
                                            <input type="text" className="form-control" onChange={e => setSearchTerm(e.target.value)} placeholder="Search for employee by email" />
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>E-Mail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    contacts.results.map((value, index) => {
                                        return (
                                            <tr key={"crow_" + index} className={formState.checkContacts?.includes(value.id) ? "checked" : ""}>
                                                <td>
                                                    <input className="form-check-input" id={"chk_" + index} checked={formState.checkContacts?.includes(value.id)} type="checkbox" onChange={e => onCheckedStateChange(e, value.id)} />
                                                </td>
                                                <td>{value.firstname + " " + value.lastname}</td>
                                                <td>{renderTypeInformations(value.contactType)}</td>
                                                <td>{value.email}</td>
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </table>

                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-md-6 text-start">
                        <button className="btn btn-primary" onClick={e => setFormState(x => ({ ...x, currentStep: 2, errors: [] }))}>
                            <i className="bi bi-arrow-left me-1" /> Previous
                        </button>
                    </div>
                    <div className="col-12 col-md-6 text-end">
                        <button className="btn btn-primary" disabled={!formState.checkContacts || formState.checkContacts.length === 0} onClick={e => setFormState(x => ({ ...x, currentStep: 4 }))}>
                            Next<i className="bi bi-arrow-right ms-1" />
                        </button>
                    </div>
                </div>

            </>
        )
    }

    /**
     * Methods renders last input mask with overview and confirmation
     * @returns
     */
    const renderFinalMask = () => {
        return (
            <>
                <div className="row">

                    <div className="col-12">
                        <h5><i className="bi bi-bookmark me-1" />Summary</h5>
                        <p>Overview of the entries you have made. Please check and correct the entries if necessary.</p>
                    </div>

                </div>

                <hr className="mt-0" />

                <div className="row">
                    <div className="col-12">
                        <button className="link" onClick={e => setFormState(x => ({ ...x, currentStep: 1 }))}>
                            <i className="bi bi-pencil me-2"/>Step 1: Event Details
                        </button>
                    </div>
                </div>

                <hr />

                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label>Event Number</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.number}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label>Event Title</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.title}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label><i className="bi bi-calendar me-2"/>Seminar Start Date</label>
                    </dt>
                    <dd className="col-1 col-md-6">{event.startDate.toDateString()}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label><i className="bi bi-calendar me-2"/>Seminar End Date</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.endDate.toDateString()}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label><i className="bi bi-calendar me-2"/>Offline Date</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.offlineDate.toDateString()}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label><i className="bi bi-flag me-2"/>Distance Band Version</label>
                    </dt>
                    <dd className="col-12 col-md-6">#{event.distanceBandVersion}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label>Max. Night</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.maxNights && event.maxNights > 0 ? event.maxNights : event.accommodationEligible ? "Calculated" : "No Applicable"}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label>Accommodation Eligible</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.accommodationEligible ? "Yes" : "No"}</dd>
                </dl>

                <hr />

                <div className="row">
                    <div className="col-12">
                        <button className="link" onClick={e => setFormState(x => ({ ...x, currentStep: 2 }))}>
                            <i className="bi bi-pencil me-2"></i>Step 2: Venue
                        </button>
                    </div>
                </div>

                <hr />

                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label><i className="bi bi-geobi bi-geo me-2"></i>Venue Name</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.location.name}</dd>
                </dl>
                <dl className="row">
                    <dt className="col-12 col-md-6">
                        <label><i className="bi bi-geobi bi-geo me-2"></i>City</label>
                    </dt>
                    <dd className="col-12 col-md-6">{event.location.city}</dd>
                </dl>

                <hr />

                <div className="row">
                    <div className="col-12">
                        <button className="link" onClick={e => setFormState(x => ({ ...x, currentStep: 3 }))}>
                            <i className="bi bi-pencil me-2"></i>Step 3: Employees
                        </button>
                    </div>
                </div>

                <hr />

                <dl className="row">
                    <dt className="col-6">
                        <i className="bi bi-person"></i> Employees
                    </dt>
                    <dd className="col-6">
                        {
                            formState.checkContacts?.flatMap(x => contacts.results.find(y => y.id === x)).map((value, index) => {
                                return (
                                    <NavLink to="#" className="badge badge-lg bg-dark me-1" key={"employee" + index} onClick={e => onCheckedStateChange(undefined, value?.id ?? -1)}>
                                        <i className="bi bi-x-circle me-2"></i>
                                        {value?.firstname + " " + value?.lastname}
                                    </NavLink>
                                )
                            })
                        }
                    </dd>
                </dl>

                <hr />

                <div className="row">
                    <div className="col-12">
                        <button className="btn btn-success w-100" onClick={onSubmit}>
                            Confirm and Create
                        </button>
                    </div>
                </div>
            </>
        )
    }

    /**
     * ...
     * @returns
     */
    const renderBody = () => {

        switch (formState.currentStep) {
            case 1: return renderFirstMask();
            case 2: return renderSecondMask();
            case 3: return renderThirdMask();
            case 4: return renderFinalMask();
        }
    }

    /**
     * ...
     * @param type
     */
    const renderTypeInformations = (type: ContactType) => {

        switch (type) {
            case ContactType.Assistant: return <span className="badge bg-dark">Assistant</span>
            case ContactType.Lawyer:    return <span className="badge bg-dark">Lawyer</span>
            case ContactType.Employee:  return <span className="badge bg-dark">Employee</span>
        }
    }

    // #endregion

    // #endregion

    // JSX Element for rendering browser

    return (
        <main className="container mb-4">

            <div className="row mb-2">
                <div className="col-12 text-end">
                    <NavLink to="/admin/events" className="btn btn-danger rounded-circle">
                        <i className="bi bi-x-lg" />
                    </NavLink>
                </div>
            </div>

            <div className="row">
                <div className="col-12">

                    <div className="card">
                        <div className="card-body">

                            { renderBody() }

                        </div>
                    </div>

                </div>
            </div>

        </main>
    )
}
export default EventCreate;